import { useEffect, useRef, useState } from "react";
import { T } from "@/components/T";
import { PL } from "@/components/PL";
import { useParams, Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";
import { fetchItem } from "@/lib/api";
import { CATEGORY_BY_TYPE } from "@/lib/categories";
import { resolveImage } from "@/lib/images";
import AudioPlayer from "@/components/AudioPlayer";
import SourcesBlock from "@/components/SourcesBlock";
import BookmarkButton from "@/components/BookmarkButton";
import ShareButton from "@/components/ShareButton";
import KnowledgeCard from "@/components/KnowledgeCard";
import RichText from "@/components/RichText";
import RelatedServices from "@/components/RelatedServices";
import ReportIssue from "@/components/ReportIssue";
import JourneyStrip from "@/components/JourneyStrip";
import PageEngage from "@/components/PageEngage";
import { Divider, MandalaMotif } from "@/components/MotifBackground";
import Seo from "@/components/Seo";
import { articleLd, breadcrumbLd, graph } from "@/lib/jsonld";
import ArticleLangScope from "@/components/ArticleLangScope";
import { ChevronRight, Clock, Maximize2, CircleDot, BookOpen } from "lucide-react";
import ReaderMode from "@/components/ReaderMode";
import { fireGamify } from "@/lib/gamify";
import { pickScript } from "@/lib/bilingual";

const JOURNEY_BY_TYPE = {
  mantra: ["deities", "stotras", "japa", "rituals", "calendar"],
  stotra: ["deities", "mantras", "rituals", "japa", "calendar"],
  sukta: ["mantras", "stotras", "deities", "rituals"],
  puja_vidhi: ["panchang", "calendar", "mantras", "stotras", "deities"],
  festival: ["panchang", "calendar", "rituals", "mantras", "deities"],
};

// Standard panchopachara offering mantras — universal to household puja.
const UPACHARA = [
  { devanagari: "ॐ गन्धं समर्पयामि।", transliteration: "oṃ gandhaṃ samarpayāmi", meaning: "I offer sandal paste (gandha)." },
  { devanagari: "ॐ पुष्पं समर्पयामि।", transliteration: "oṃ puṣpaṃ samarpayāmi", meaning: "I offer flowers (pushpa)." },
  { devanagari: "ॐ धूपमाघ्रापयामि।", transliteration: "oṃ dhūpam āghrāpayāmi", meaning: "I offer incense (dhupa)." },
  { devanagari: "ॐ दीपं दर्शयामि।", transliteration: "oṃ dīpaṃ darśayāmi", meaning: "I show the lamp (deepa)." },
  { devanagari: "ॐ नैवेद्यं निवेदयामि।", transliteration: "oṃ naivedyaṃ nivedayāmi", meaning: "I offer the food (naivedya)." },
];

const stdSankalpa = (deity) =>
  `ॐ विष्णुर्विष्णुर्विष्णुः।\nअद्य (देश-काल-तिथि-वार-नक्षत्रोच्चारणपूर्वकम्) (नाम)-नामाहं (गोत्र)-गोत्रः\nमम आत्मनः श्रुतिस्मृतिपुराणोक्तफलप्राप्त्यर्थं${deity ? " श्री" + deity + "प्रीत्यर्थं" : ""} यथाशक्ति पूजनम् अहं करिष्ये॥`;

// Detect real rich formatting (headings/lists/emphasis) vs simple <p>-wrapped text.
const hasRichFormatting = (s) => /<\/?(h[1-6]|ul|ol|li|strong|b|em|i|u|mark|blockquote|a)\b/i.test(s || "");
// Strip light HTML (from the TipTap editor) back to plain text for verse parsing.
const stripHtml = (s) =>
  s ? s.replace(/<br\s*\/?>/gi, "\n").replace(/<\/(p|div|h[1-6])>/gi, "\n").replace(/<[^>]+>/g, "").replace(/&nbsp;/gi, " ").replace(/&amp;/gi, "&").trim() : "";

// Split a scripture string into verses, capturing the (Devanagari or latin) verse number.
const parseVerses = (text) => {
  if (!text) return [];
  const re = /([\s\S]*?)(?:॥|\|\|)\s*([०-९0-9]+)\s*(?:॥|\|\|)/g;
  const out = [];
  let m, last = 0;
  while ((m = re.exec(text)) !== null) {
    out.push({ body: m[1].trim(), num: m[2] });
    last = re.lastIndex;
  }
  const rest = text.slice(last).trim().replace(/॥|\|\|/g, "").trim();
  if (out.length === 0) {
    return text
      .split(/॥|\|\|/)
      .map((s) => s.trim())
      .filter(Boolean)
      .map((body) => ({ body, num: null }));
  }
  if (rest) out.push({ body: rest, num: null });
  return out;
};

const heroFade = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

const SectionHeading = ({ deva, en }) => (
  <div className="mb-6 flex flex-wrap items-center gap-x-3 gap-y-1">
    <span className="h-px w-8 shrink-0 bg-[var(--gold)]" />
    <h3 className="shrink-0 whitespace-nowrap font-deva text-2xl text-saffron deva-lh md:text-3xl">{deva}</h3>
    {en && <span className="overline-label text-ink-3">{en}</span>}
  </div>
);

const Section = ({ id, deva, en, value }) =>
  value ? (
    <section id={id} data-section className="scroll-mt-28 border-t border-[var(--line)] pt-12 mt-12 fade-up">
      <SectionHeading deva={deva} en={en} />
      <RichText value={value} className="reading-content max-w-[68ch] font-serif-latin text-lg leading-[1.9] text-ink-2" />
    </section>
  ) : null;

export default function KnowledgeDetail() {
  return (
    <ArticleLangScope testid="knowledge">
      <KnowledgeDetailInner />
    </ArticleLangScope>
  );
}

function KnowledgeDetailInner() {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t, L, pick, pickTitle, pickSecondary, pickDesc, isDeva, lang } = useLang();
  const { copy } = useSettings();
  // Admin-overridable detail-page template labels (Admin → Page Titles → Detail page labels).
  const C = (key, en, hi) => { const o = copy(key, en, hi); return pick(o.hi, o.en); };
  const [item, setItem] = useState(null);
  const [notFound, setNotFound] = useState(false);
  // Intro is clamped to 4 lines; offer "Read more" only when it is actually cut.
  const introRef = useRef(null);
  const [introOpen, setIntroOpen] = useState(false);
  const [introClamped, setIntroClamped] = useState(false);
  useEffect(() => {
    setIntroOpen(false);
    const el = introRef.current;
    if (!el) return;
    const t = setTimeout(() => setIntroClamped(el.scrollHeight > el.clientHeight + 2), 50);
    return () => clearTimeout(t);
  }, [item, lang]);
  const [readerOpen, setReaderOpen] = useState(false);
  const [active, setActive] = useState(null);
  const progressRef = useRef(null);

  useEffect(() => {
    setItem(null);
    setNotFound(false);
    fetchItem(slug).then((data) => {
      if (data.slug && data.slug !== slug) {
        navigate(location.pathname.replace(/[^/]+$/, data.slug), { replace: true });
        return;
      }
      setItem(data);
      fireGamify("read", data.slug || slug);
    }).catch(() => setNotFound(true));
  }, [slug, location.pathname]);

  // Reading progress bar — update the bar width directly via a ref, throttled
  // with requestAnimationFrame, so scrolling never re-renders this large page.
  useEffect(() => {
    let ticking = false;
    const update = () => {
      ticking = false;
      const h = document.documentElement;
      const denom = h.scrollHeight - h.clientHeight;
      const pct = denom > 0 ? Math.min(1, Math.max(0, h.scrollTop / denom)) * 100 : 0;
      if (progressRef.current) progressRef.current.style.width = `${pct}%`;
    };
    const onScroll = () => {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    update();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Scroll-spy for the in-page table of contents
  useEffect(() => {
    if (!item) return;
    const els = Array.from(document.querySelectorAll("[data-section]"));
    if (!els.length) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); });
      },
      { rootMargin: "-15% 0px -70% 0px", threshold: 0 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [item, lang]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 96, behavior: "smooth" });
  };

  if (notFound) return <div className="mx-auto max-w-3xl px-5 py-32 text-center text-ink-3"><T>Entry not found.</T></div>;
  if (!item) return <div className="mx-auto max-w-3xl px-5 py-40 text-center font-deva text-3xl text-gold/50">ॐ</div>;

  const cat = CATEGORY_BY_TYPE[item.type];
  const isMantraLike = ["mantra", "stotra", "sukta"].includes(item.type);
  const isPuja = item.type === "puja_vidhi";
  const title = pickTitle(item);
  const secondaryTitle = pickSecondary(item);
  const shortDesc = pickDesc(item);
  const heroImage = resolveImage(item);

  const richSanskrit = isMantraLike && hasRichFormatting(item.sanskrit_text);
  const verses = isMantraLike && !richSanskrit ? parseVerses(stripHtml(item.sanskrit_text)) : [];
  const iastVerses = isMantraLike && !richSanskrit ? parseVerses(stripHtml(item.transliteration)) : [];
  const paired = verses.length > 0 && iastVerses.length === verses.length;
  // Full Sanskrit + transliteration carried into the Japa page so the chanter
  // sees the complete mantra, not just the title. Falls back to the title fields.
  const japaDeva = (stripHtml(item.sanskrit_text) || "").trim() || item.devanagari_title || title;
  const japaTranslit = (stripHtml(item.transliteration) || "").trim() || item.title || "";
  const hasScripture = isMantraLike && (verses.length > 0 || richSanskrit);

  const meta = [
    ["Deity", "देवता", item.deity, item.deities?.length ? `/deities/${item.deities[0]}` : null],
    ["Tradition", "परम्परा", item.tradition],
    ["Purpose", "प्रयोजन", item.purpose],
    ["Occasion", "अवसर", item.occasion],
    ["Source", "स्रोत", item.source],
  ].filter(([, , v]) => v);

  const benefitsList = item.benefits && (lang === "hi" ? item.benefits.hi : item.benefits.en);

  // Build the table-of-contents from the sections that will actually render.
  const toc = [];
  if (hasScripture) toc.push(["moola", "मूल पाठ", "Sanskrit"]);
  if (L(item.meaning)) toc.push(["meaning", "अर्थ", "Meaning"]);
  if (L(item.context)) toc.push(["context", "संदर्भ", "Context"]);
  if (L(item.usage)) toc.push(["usage", "प्रयोग", "Usage"]);
  if (L(item.body)) toc.push(["overview", "विवरण", "Overview"]);
  if (isPuja) {
    if (item.muhurta && (item.muhurta.sthapana_date || item.muhurta.visarjan_date)) toc.push(["muhurta", "मुहूर्त", "Muhurta"]);
    if (item.materials?.length > 0) toc.push(["materials", "सामग्री", "Materials"]);
    if (L(item.preparation)) toc.push(["prep", "तैयारी", "Preparation"]);
    toc.push(["sankalpa", "संकल्प", "Sankalpa"]);
    if (item.steps?.length > 0) toc.push(["steps", "पूजा विधि", "Procedure"]);
    if (item.mantras?.length > 0) toc.push(["mantras", "मंत्र", "Mantras"]);
    toc.push(["upachara", "उपचार", "Offerings"]);
    if (item.ganesh_names_21?.length > 0) toc.push(["names21", "२१ नाम", "21 Names"]);
    if (item.aarti) toc.push(["aarti", "आरती", "Arati"]);
    if (item.namaskara || item.pushpanjali || item.final_prayer) toc.push(["prayer", "प्रार्थना", "Prayer"]);
    if (benefitsList?.length > 0) toc.push(["benefits", "फल", "Benefits"]);
    if (item.sequence?.length > 0) toc.push(["sequence", "क्रम", "Sequence"]);
    if (L(item.conclusion)) toc.push(["conclusion", "समापन", "Conclusion"]);
  }

  return (
    <article className="relative pb-24">
      <Seo
        path={`${cat.path}/${item.slug}`}
        title={item.seo_title || title}
        description={item.seo_description || shortDesc}
        keywords={item.seo_keywords}
        image={item.preview_image || heroImage}
        type="article"
        jsonLd={graph(
          articleLd({ title: item.seo_title || title, description: item.seo_description || shortDesc, image: item.preview_image || heroImage, path: `${cat.path}/${item.slug}`, datePublished: item.created_at, dateModified: item.updated_at }),
          breadcrumbLd([{ name: "Dharmartha", path: "/" }, { name: cat.en || "Knowledge", path: cat.path }, { name: title, path: `${cat.path}/${item.slug}` }])
        )}
      />

      {/* Reading progress */}
      <div ref={progressRef} className="fixed left-0 top-0 z-[60] h-[3px] bg-saffron" style={{ width: "0%" }} aria-hidden="true" />


      {/* ————— Content hero (image as a clean card, never behind text) ————— */}
      <header className="relative border-b border-[var(--line)] bg-[var(--surface)]">
        <div className="mx-auto max-w-3xl px-5 pb-10 pt-8 md:px-8 md:pb-14 md:pt-12">
          <motion.div
            initial="hidden"
            animate="show"
            variants={{ show: { transition: { staggerChildren: 0.08 } } }}
          >
            <motion.nav variants={heroFade} className="mb-5 flex items-center gap-1.5 truncate text-xs text-ink-3">
              <Link to="/" className="shrink-0 transition-colors hover:text-saffron"><T>Dharmartha</T></Link>
              <ChevronRight className="h-3 w-3 shrink-0" />
              <Link to={cat.path} className="truncate transition-colors hover:text-saffron">{cat.en}</Link>
            </motion.nav>

            {heroImage ? (
              <motion.div variants={heroFade} className="mb-6 overflow-hidden rounded-[20px] border border-[var(--line)] bg-[var(--parchment)] shadow-[0_18px_44px_-30px_rgba(0,0,0,0.5)]">
                <img src={heroImage} alt={title} loading="eager" className="aspect-[16/9] w-full object-cover object-center" />
              </motion.div>
            ) : (
              <motion.div variants={heroFade} className="mb-6 flex h-16 w-16 items-center justify-center rounded-full border border-[var(--gold-muted)]/50 bg-[rgba(184,134,43,0.08)]">
                <BookOpen className="h-7 w-7 text-gold" />
              </motion.div>
            )}

            <motion.p variants={heroFade} data-testid="detail-category" className="inline-block rounded-full border border-[var(--gold-muted)]/55 bg-[rgba(184,134,43,0.08)] px-3 py-1 text-xs font-medium text-gold">
              {item.categories?.length ? (
                <Link to={`/categories/${item.categories[0]}`} data-testid="detail-category-link" className="transition-colors hover:text-saffron">
                  {item.category || cat.en}
                </Link>
              ) : (item.category || cat.en)}
            </motion.p>

            <motion.h1
              variants={heroFade}
              data-testid="detail-title"
              className={`mt-4 ${isDeva ? "font-deva" : "font-serif-latin"} text-4xl leading-tight tracking-tight text-ink deva-lh wrap-anywhere md:text-5xl`}
            >
              {title}
            </motion.h1>

            {secondaryTitle && (
              <motion.p variants={heroFade} className={`mt-3 text-xl text-ink-2 wrap-anywhere md:text-2xl ${lang === "hi" ? "font-serif-latin" : "font-deva deva-lh"}`}>
                {secondaryTitle}
              </motion.p>
            )}

            {(L(item.intro) || L(item.detail_heading) || shortDesc) && (
              <motion.div variants={heroFade} className="mt-5 max-w-[62ch]">
                {L(item.detail_heading) && <p className="mb-2 overline-label text-saffron" data-testid="detail-intro-heading">{L(item.detail_heading)}</p>}
                <div ref={introRef} data-testid="detail-intro" className={`text-[17px] leading-[1.7] text-ink-2${introOpen ? "" : " line-clamp-4"}`}>
                  {L(item.intro) ? <RichText value={L(item.intro)} /> : shortDesc}
                </div>
                {(introClamped || introOpen) && (
                  <button type="button" data-testid="detail-intro-toggle" onClick={() => setIntroOpen((v) => !v)} className="mt-2 text-sm font-medium text-saffron hover:underline">
                    <PL>{introOpen ? "Show less" : "Read more"}</PL>
                  </button>
                )}
              </motion.div>
            )}

            <motion.div variants={heroFade} className="mt-7 flex flex-wrap items-center gap-3">
              <BookmarkButton slug={item.slug} title={title} />
              <ShareButton title={title} text={`${title} — Dharmartha`} label={t("share")} />
              {item.type === "mantra" && (
                <Link
                  to={`/japa?m=${encodeURIComponent("c-" + item.slug)}&deva=${encodeURIComponent(japaDeva)}&translit=${encodeURIComponent(japaTranslit)}&meaning=${encodeURIComponent(item.english_title || pickDesc(item).slice(0, 80) || "")}&label=${encodeURIComponent(item.devanagari_title || item.title || title)}`}
                  data-testid="chant-in-japa-btn"
                  className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full border border-saffron px-4 py-1.5 text-sm font-medium text-saffron transition-colors hover:bg-[var(--saffron)] hover:text-white"
                >
                  <CircleDot className="h-4 w-4" /> {pick("जप करें", "Chant in Japa")}
                </Link>
              )}
              {item.read_time && (
                <span className="inline-flex items-center gap-1.5 text-sm text-ink-3">
                  <Clock className="h-4 w-4" /> {item.read_time}
                </span>
              )}
            </motion.div>
          </motion.div>
        </div>
      </header>

      {/* ————— Editorial 3 / 7 / 2 grid ————— */}
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="grid grid-cols-12 gap-x-0 gap-y-8 lg:gap-x-8 lg:gap-y-0 xl:gap-x-12">
          {/* Left: sticky table of contents */}
          <aside className="col-span-3 hidden min-w-0 lg:block" data-testid="detail-toc">
            {toc.length > 1 && (
              <nav className="sticky top-24 pt-14">
                <p className="overline-label mb-4 pl-4 text-ink-3">{C("detail.on_this_page", "On this page", "इस पृष्ठ पर")}</p>
                <ul className="border-l border-[var(--line)]">
                  {toc.map(([id, deva, en]) => (
                    <li key={id}>
                      <button
                        onClick={() => scrollTo(id)}
                        className={`-ml-px block w-full border-l-2 py-1.5 pl-4 text-left text-sm transition-colors ${
                          active === id ? "border-[var(--saffron)] text-saffron" : "border-transparent text-ink-3 hover:text-ink"
                        }`}
                      >
                        {pick(deva, en)}
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
            )}
          </aside>

          {/* Center: reading measure */}
          <div className="col-span-12 min-w-0 pt-8 lg:col-span-7 lg:pt-14 xl:col-span-6">
            {/* Metadata strip */}
            {meta.length > 0 && (
              <dl data-testid="meta-strip" className="grid grid-cols-2 gap-x-6 gap-y-5 border-y border-[var(--line)] py-6 sm:grid-cols-3">
                {meta.map(([en, deva, value, to]) => (
                  <div key={en}>
                    <dt className="overline-label text-ink-3">{pick(deva, en)}</dt>
                    <dd className="mt-1.5 font-serif-latin text-[15px] leading-snug text-ink">
                      {to ? (
                        <Link to={to} data-testid="meta-deity-link" className="underline-offset-4 transition-colors hover:text-saffron hover:underline">{value}</Link>
                      ) : value}
                    </dd>
                  </div>
                ))}
              </dl>
            )}

            {/* Scripture reader */}
            {hasScripture && (
              <section id="moola" data-section className="mt-12 scroll-mt-28">
                <SectionHeading deva="मूल पाठ" en="Sanskrit text" />
                {richSanskrit ? (
                  <div className="group relative border border-[var(--line)] bg-[var(--surface)] px-5 py-9 sm:px-8 md:px-12">
                    <span className="absolute left-0 top-0 h-full w-0.5 bg-[var(--saffron)] opacity-50 transition-opacity group-hover:opacity-100" />
                    <RichText value={item.sanskrit_text} className="reading-content text-center font-deva text-2xl leading-[2.1] text-ink sm:text-[28px]" />
                    {item.transliteration && (
                      <>
                        <div className="mx-auto my-6 h-px w-12 bg-[var(--line)]" />
                        <RichText value={item.transliteration} className="tg-note reading-content mx-auto max-w-[58ch] text-center font-serif-latin text-lg italic leading-[1.9] text-ink-2" />
                      </>
                    )}
                  </div>
                ) : (
                  <div className="space-y-5">
                    {verses.map((v, i) => (
                      <div key={i} className="group relative border border-[var(--line)] bg-[var(--surface)] px-4 py-6 sm:px-10 sm:py-10">
                        <span className="absolute left-0 top-0 h-full w-0.5 bg-[var(--saffron)] opacity-40 transition-opacity group-hover:opacity-100" />
                        <p className="hyphens-none whitespace-pre-line wrap-anywhere text-center font-deva text-2xl leading-[2] text-ink sm:text-[28px]">{v.body}</p>
                        {paired && iastVerses[i]?.body && (
                          <>
                            <div className="mx-auto my-5 h-px w-12 bg-[var(--line)]" />
                            <p className="mx-auto max-w-[58ch] whitespace-pre-line wrap-anywhere text-center font-serif-latin text-lg italic leading-[1.9] text-ink-2">{iastVerses[i].body}</p>
                          </>
                        )}
                        {v.num && <p className="mt-5 text-center font-deva text-lg text-saffron">॥&nbsp;{v.num}&nbsp;॥</p>}
                      </div>
                    ))}
                  </div>
                )}

                {/* Transliteration (only when it could not be paired verse-by-verse) */}
                {!richSanskrit && !paired && item.transliteration && (
                  <div className="mt-6 border-l-2 border-[var(--gold)] pl-5">
                    <p className="overline-label text-ink-3">{C("detail.transliteration", "Transliteration (IAST)", "लिप्यंतरण (IAST)")}</p>
                    <RichText value={item.transliteration} className="tg-note reading-content mt-2 font-serif-latin text-lg italic leading-relaxed text-ink-2" />
                  </div>
                )}

                {verses.length > 0 && (
                  <div className="mt-8">
                    <button
                      data-testid="open-reader-btn"
                      onClick={() => setReaderOpen(true)}
                      className="inline-flex items-center gap-2 border border-line px-4 py-2 text-sm text-ink-2 transition-colors hover:border-[var(--ink)] hover:text-ink"
                    >
                      <Maximize2 className="h-4 w-4" /> {pick("पूर्ण-स्क्रीन पाठ", "Focus reader")}
                    </button>
                  </div>
                )}
                <div className="mt-4">
                  <AudioPlayer label={`${title} — ${pick("पाठ", "recitation")}`} src={item.audio_url} available={!!item.audio_url} />
                </div>
              </section>
            )}

            <Section id="meaning" deva="अर्थ" en="Meaning" value={L(item.meaning)} />
            <Section id="context" deva="संदर्भ" en="Context" value={L(item.context)} />
            <Section id="usage" deva="प्रयोग" en="How it is traditionally used" value={L(item.usage)} />
            <Section id="overview" deva="विवरण" en="Overview" value={L(item.body)} />

            {/* Pooja Vidhi */}
            {isPuja && (
              <>
                {item.muhurta && (item.muhurta.sthapana_date || item.muhurta.visarjan_date) && (
                  <section id="muhurta" data-section data-testid="muhurta-banner" className="mt-12 scroll-mt-28 fade-up">
                    <div className="relative overflow-hidden border border-[var(--gold)]/40 bg-gradient-to-br from-[rgba(184,134,11,0.06)] via-[var(--surface-alt)]/70 to-[rgba(199,80,0,0.05)] p-6 md:p-8">
                      <p className="overline-label text-saffron">{C("detail.muhurta", "Auspicious Muhurta", "शुभ मुहूर्त")}</p>
                      <h3 className={`mt-2 text-2xl text-ink deva-lh md:text-3xl ${lang === "hi" ? "font-deva" : "font-serif-latin"}`}>{lang === "hi" ? (item.muhurta.label_hi || item.muhurta.label_en) : (item.muhurta.label_en || item.muhurta.label_hi)}</h3>
                      <div className="mt-5 grid gap-5 md:grid-cols-2">
                        {item.muhurta.sthapana_date && (
                          <div className="border-l-2 border-[var(--gold)] pl-4">
                            <p className="overline-label text-ink-3">{pick("स्थापना", "Sthapana")}</p>
                            <p className="mt-1.5 font-serif-latin text-lg text-ink">{item.muhurta.sthapana_date}</p>
                            {item.muhurta.sthapana_window && <p className="mt-1 text-sm text-ink-2">{item.muhurta.sthapana_window}</p>}
                          </div>
                        )}
                        {item.muhurta.visarjan_date && (
                          <div className="border-l-2 border-saffron pl-4">
                            <p className="overline-label text-ink-3">{pick("विसर्जन", "Visarjan")}</p>
                            <p className="mt-1.5 font-serif-latin text-lg text-ink">{item.muhurta.visarjan_date}</p>
                            {item.muhurta.visarjan_window && <p className="mt-1 text-sm text-ink-2">{item.muhurta.visarjan_window}</p>}
                          </div>
                        )}
                      </div>
                      {(lang === "hi" ? item.muhurta.notes_hi : item.muhurta.notes_en) && (
                        <p className="mt-5 text-[13px] italic text-ink-3">{lang === "hi" ? item.muhurta.notes_hi : item.muhurta.notes_en}</p>
                      )}
                    </div>
                  </section>
                )}

                {item.materials?.length > 0 && (
                  <section id="materials" data-section className="mt-12 scroll-mt-28 border-t border-[var(--line)] pt-12 fade-up">
                    <SectionHeading deva="सामग्री" en="Required materials" />
                    <ul className="grid gap-2.5 sm:grid-cols-2">
                      {item.materials.map((m, i) => (
                        <li key={i} className="flex items-start gap-2.5 text-[17px] text-ink-2">
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rotate-45 bg-[var(--gold)]" />{pickScript(m, lang)}
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                <Section id="prep" deva="पूर्व तैयारी" en="Preparation" value={L(item.preparation)} />

                <section id="sankalpa" data-section className="mt-12 scroll-mt-28 border-t border-[var(--line)] pt-12 fade-up">
                  <SectionHeading deva="संकल्प" en="Sankalpa" />
                  <div className="border-l-2 border-[var(--gold)] bg-[var(--surface-alt)]/50 px-6 py-5">
                    <p className="whitespace-pre-line font-deva text-lg leading-relaxed text-ink">{item.sankalpa || stdSankalpa(item.deity)}</p>
                    <p className="mt-3 text-xs text-ink-3">{pick("दाहिने हाथ में जल, पुष्प एवं अक्षत लेकर संकल्प का उच्चारण करें, फिर जल छोड़ दें।", "Recite the sankalpa holding water, flowers and rice in the right palm; then release the water.")}</p>
                  </div>
                </section>

                {item.steps?.length > 0 && (
                  <section id="steps" data-section className="mt-12 scroll-mt-28 border-t border-[var(--line)] pt-12 fade-up">
                    <SectionHeading deva="पूजा विधि" en="Step-by-step procedure" />
                    <ol className="relative space-y-8 border-l border-[var(--line)] pl-8 lg:pl-12">
                      {item.steps.map((s, i) => (
                        <li key={i} data-testid={`puja-step-${i}`} className="relative">
                          <span className="absolute -left-[33px] top-0 flex h-8 w-8 items-center justify-center rounded-full border-2 border-[var(--saffron)] bg-[var(--parchment)] font-serif-latin text-[13px] font-bold text-saffron lg:-left-[49px]">{i + 1}</span>
                          <p className={`text-lg text-ink ${lang === "hi" ? "font-deva" : "font-serif-latin"}`}>{pickScript(s.title, lang)}</p>
                          <p className="mt-2 max-w-[62ch] whitespace-pre-line leading-relaxed text-ink-2">{pickScript(s.text, lang)}</p>
                        </li>
                      ))}
                    </ol>
                  </section>
                )}

                {item.mantras?.length > 0 && (
                  <section id="mantras" data-section className="mt-12 scroll-mt-28 border-t border-[var(--line)] pt-12 fade-up">
                    <SectionHeading deva="मंत्र" en="Mantras" />
                    <div className="space-y-4">
                      {item.mantras.map((m, i) => (
                        <div key={i} className="border-l-2 border-[var(--gold)] bg-[var(--surface-alt)]/50 px-6 py-5">
                          <p className="whitespace-pre-line wrap-anywhere font-deva text-xl leading-[1.9] text-ink">{m.devanagari}</p>
                          {m.transliteration && <p className="mt-1.5 wrap-anywhere font-serif-latin text-base italic text-ink-2">{m.transliteration}</p>}
                          {m.meaning && <p className="mt-2.5 text-base text-ink-2">{m.meaning}</p>}
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                <section id="upachara" data-section className="mt-12 scroll-mt-28 border-t border-[var(--line)] pt-12 fade-up">
                  <SectionHeading deva="उपचार" en="Offerings (Upachara)" />
                  <div className="space-y-3">
                    {UPACHARA.map((m, i) => (
                      <div key={i} className="border-l-2 border-[var(--gold)] bg-[var(--surface-alt)]/50 px-6 py-4">
                        <p className="font-deva text-xl text-ink">{m.devanagari}</p>
                        <p className="mt-1 font-serif-latin text-base italic text-ink-2">{m.transliteration}</p>
                        <p className="mt-1.5 text-base text-ink-2">{m.meaning}</p>
                      </div>
                    ))}
                  </div>
                  <p className="mt-3 text-xs text-ink-3">{pick("पञ्चोपचार — प्रत्येक वस्तु के अर्पण के साथ सम्बन्धित मंत्र का उच्चारण करें।", "Standard panchopachara — offer each item while reciting the corresponding mantra.")}</p>
                </section>

                {item.ganesh_names_21?.length > 0 && (
                  <section id="names21" data-section className="mt-12 scroll-mt-28 border-t border-[var(--line)] pt-12 fade-up">
                    <SectionHeading deva="२१ गणेश नाम" en="The 21 Names (offered with Durva)" />
                    <div className="grid gap-x-6 gap-y-2.5 sm:grid-cols-2">
                      {item.ganesh_names_21.map((n, i) => (
                        <div key={i} className="flex items-baseline gap-2.5">
                          <span className="font-serif-latin text-xs text-gold">{i + 1}</span>
                          <span className="font-deva text-lg text-ink deva-lh">{n}</span>
                        </div>
                      ))}
                    </div>
                    <p className="mt-3 text-xs text-ink-3">{pick("प्रत्येक नाम के साथ एक दूर्वा-दल अर्पित करें।", "Offer one blade of durva grass with each name.")}</p>
                  </section>
                )}

                {item.aarti && (
                  <section id="aarti" data-section data-testid="puja-aarti" className="mt-12 scroll-mt-28 border-t border-[var(--line)] pt-12 fade-up">
                    <SectionHeading deva="आरती" en="Arati" />
                    <div className="relative border-l-2 border-[var(--gold)] bg-[var(--surface-alt)]/50 px-6 py-7 md:px-10">
                      <p className="whitespace-pre-line wrap-anywhere font-deva text-xl leading-[2] text-ink md:text-2xl">{item.aarti}</p>
                    </div>
                    <p className="mt-3 text-xs text-ink-3">{pick("पाँच-मुखी दीप अथवा कर्पूर से आरती करें, तत्पश्चात् — ॐ श्रीगणपतये नमः। कर्पूरनीराजनं समर्पयामि॥", "Perform arati with a five-wick lamp or camphor, then — ॐ श्रीगणपतये नमः। कर्पूरनीराजनं समर्पयामि॥")}</p>
                  </section>
                )}

                {(item.namaskara || item.pushpanjali || item.final_prayer) && (
                  <section id="prayer" data-section className="mt-12 scroll-mt-28 border-t border-[var(--line)] pt-12 fade-up">
                    <SectionHeading deva="नमस्कार एवं प्रार्थना" en="Namaskara & Prayer" />
                    <div className="grid gap-4 md:grid-cols-2">
                      {[["नमस्कार", "Namaskara", item.namaskara], ["पुष्पांजलि", "Pushpanjali", item.pushpanjali], ["अन्तिम प्रार्थना", "Final Prayer", item.final_prayer]].filter(([, , v]) => v).map(([deva, en, v]) => (
                        <div key={en} className="border-l-2 border-[var(--gold)] bg-[var(--surface-alt)]/50 px-6 py-5">
                          <p className="overline-label text-ink-3">{pick(deva, en)}</p>
                          <p className="mt-2.5 whitespace-pre-line font-deva text-lg leading-[1.9] text-ink">{v}</p>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {benefitsList?.length > 0 && (
                  <section id="benefits" data-section className="mt-12 scroll-mt-28 border-t border-[var(--line)] pt-12 fade-up">
                    <SectionHeading deva="पारम्परिक फल" en="Traditional benefits" />
                    <p className="max-w-[62ch] text-ink-2">{pick("परम्परागत हिन्दू मान्यता में इस उपासना को इन भावनाओं से जोड़ा जाता है:", "In traditional Hindu belief, this worship is associated with:")}</p>
                    <ul className="mt-5 grid gap-2.5 sm:grid-cols-2">
                      {benefitsList.map((b, i) => (
                        <li key={i} className="flex items-start gap-2.5 text-[17px] text-ink-2">
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rotate-45 bg-[var(--gold)]" />{b}
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {item.sequence?.length > 0 && (
                  <section id="sequence" data-section data-testid="puja-sequence" className="mt-12 scroll-mt-28 border-t border-[var(--line)] pt-12 fade-up">
                    <SectionHeading deva="पूजा का सम्पूर्ण क्रम" en="The complete sequence" />
                    <ol className="mt-2 columns-1 gap-x-12 sm:columns-2">
                      {item.sequence.map(([deva, en], i) => (
                        <li key={i} className="flex items-baseline gap-3 break-inside-avoid border-b border-[var(--line)]/50 py-2.5">
                          <span className="w-7 shrink-0 font-serif-latin text-sm italic text-gold/80">{i + 1}</span>
                          <span className={`text-[15px] leading-snug text-ink ${lang === "hi" ? "font-deva" : "font-serif-latin"}`}>{pick(deva, en)}</span>
                        </li>
                      ))}
                    </ol>
                  </section>
                )}

                {L(item.conclusion) && (
                  <section id="conclusion" data-section data-testid="visarjan-section" className="mt-12 scroll-mt-28 border-t border-[var(--line)] pt-12 fade-up">
                    <SectionHeading deva={item.muhurta?.visarjan_date ? "विसर्जन एवं समापन" : "समापन"} en={item.muhurta?.visarjan_date ? "Visarjan & Conclusion" : "Conclusion"} />
                    <div className={`relative overflow-hidden ${item.muhurta?.visarjan_date ? "border border-saffron/30 bg-[rgba(199,80,0,0.03)] p-6 md:p-8" : ""}`}>
                      <RichText value={L(item.conclusion)} className="reading-content max-w-[68ch] font-serif-latin text-[17px] leading-[1.9] text-ink-2 md:text-lg" />
                      {item.muhurta?.visarjan_date && (
                        <p className="mt-4 font-deva text-lg text-saffron">गणपति बाप्पा मोरया · मंगलमूर्ति मोरया</p>
                      )}
                    </div>
                  </section>
                )}
              </>
            )}

            <SourcesBlock item={item} />
            <ReportIssue area={title} />
          </div>

          {/* Right rail: framed deity image (no crop feel) + quick actions */}
          <aside className="col-span-2 hidden min-w-0 lg:block xl:col-span-3">
            <div className="sticky top-24 space-y-5 pt-14">
              <figure className="group overflow-hidden border border-[var(--line)] bg-[var(--surface)]">
                <div className="relative aspect-[3/4] overflow-hidden">
                  <img src={heroImage} alt={title} className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                  <MandalaMotif className="pointer-events-none absolute -bottom-8 -right-8 h-40 w-40 text-white opacity-20 mix-blend-overlay" />
                </div>
                <figcaption className="p-4">
                  <p className="overline-label text-gold">
                    {item.categories?.length ? (
                      <Link to={`/categories/${item.categories[0]}`} className="underline-offset-4 transition-colors hover:text-saffron hover:underline">{item.category || cat.en}</Link>
                    ) : (item.category || cat.en)}
                  </p>
                  <p className={`mt-1.5 leading-snug text-ink ${isDeva ? "font-deva text-lg" : "font-serif-latin text-xl"}`}>{title}</p>
                </figcaption>
              </figure>

              {item.audio_url && (
                <div className="border border-[var(--line)] bg-[var(--surface)] p-4">
                  <p className="overline-label mb-3 text-ink-3">{C("detail.listen", "Listen", "श्रवण")}</p>
                  <AudioPlayer label={pick("पाठ", "recitation")} src={item.audio_url} available={!!item.audio_url} />
                </div>
              )}

              <div className="flex items-center gap-3">
                <BookmarkButton slug={item.slug} title={title} variant="icon" />
                <ShareButton title={title} text={`${title} — Dharmartha`} variant="icon" testid="share-button-rail" />
              </div>
            </div>
          </aside>
        </div>

      </div>

      <PageEngage refType="content" refSlug={item.slug} refTitle={title} />

      <div className="mx-auto max-w-7xl px-5 md:px-8">
        {item.related_items?.length > 0 && (
          <section className="mt-16">
            <Divider className="mb-10" />
            <h3 className="mb-8 font-serif-latin text-2xl text-ink">{t("related")}</h3>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {item.related_items.map((r) => (
                <KnowledgeCard key={r.slug} item={r} />
              ))}
            </div>
          </section>
        )}

        <RelatedServices type={item.type} deity={item.deity} />
        <JourneyStrip links={JOURNEY_BY_TYPE[item.type] || ["deities", "mantras", "rituals", "calendar"]} />
      </div>

      <ReaderMode open={readerOpen} onClose={() => setReaderOpen(false)} title={title} verses={paired ? verses.map((v, i) => ({ ...v, iast: iastVerses[i]?.body })) : verses} />
    </article>
  );
}
